import type { SelectOption } from '@diamond/shared/types';

import type {
  GeneralFormValues,
  HighlightFormValues,
  ProductListItem,
  ShipToFormValues,
  UpdateProductHighlightPayload,
} from '../types';
import type { CreateProductHighlightPayload } from '../types';

export const extractProductIds = (products: ProductListItem[]): string[] =>
  products.map((p) => p.product_id);

const asCodeArray = (arr?: ReadonlyArray<SelectOption | string>): string[] => {
  if (!arr) return [];
  return arr
    .map((item) => (typeof item === 'string' ? item : item.value))
    .filter((v): v is string => Boolean(v));
};

const toBusinessTypeObjects = (
  codes: ReadonlyArray<string> | undefined,
  businessTypeMap?: Record<string, string>
):
  | Array<{ cg_3_business_type: string; cg_3_business_type_desc: string }>
  | undefined => {
  if (!codes || codes.length === 0) return undefined;
  return codes.map((code) => ({
    cg_3_business_type: code,
    cg_3_business_type_desc: businessTypeMap?.[code] || '',
  }));
};

export const mapFormToCreatePayload = (
  values: HighlightFormValues,
  index: number,
  businessTypeMap?: Record<string, string>
): CreateProductHighlightPayload => {
  const products = [...extractProductIds(values.products)].reverse();

  // Map form mode to product_highlight_type and type
  const product_highlight_type =
    values.mode === 'general' ? 'general' : 'specific';
  const type = product_highlight_type;

  if (values.mode === 'shipto') {
    const v = values as ShipToFormValues;
    return {
      index,
      title: v.title,
      products,
      distribution_channel: asCodeArray(v.distribution_channel),
      business_segmentation: asCodeArray(v.business_segmentation),
      product_highlight_type,
      type,
      business_types: toBusinessTypeObjects(v.business_types, businessTypeMap),
      shiptos: v.shiptos,
    } as CreateProductHighlightPayload;
  }

  const v = values as GeneralFormValues;
  return {
    index,
    title: v.title,
    products,
    distribution_channel: asCodeArray(v.distribution_channel),
    business_segmentation: asCodeArray(v.business_segmentation),
    product_highlight_type,
    type,
    business_types: toBusinessTypeObjects(v.business_types, businessTypeMap),
    shiptos: v.shiptos,
  } as CreateProductHighlightPayload;
};

export const mapFormToUpdatePayload = (
  values: HighlightFormValues,
  businessTypeMap?: Record<string, string>
): UpdateProductHighlightPayload => {
  // Map form mode to product_highlight_type and type
  const product_highlight_type =
    values.mode === 'general' ? 'general' : 'specific';
  const type = product_highlight_type;

  if (values.mode === 'shipto') {
    const v = values as ShipToFormValues;
    return {
      title: v.title,
      distribution_channel: asCodeArray(v.distribution_channel),
      business_segmentation: asCodeArray(v.business_segmentation),
      product_highlight_type,
      type,
      business_types: toBusinessTypeObjects(v.business_types, businessTypeMap),
      shiptos: v.shiptos,
    };
  }

  const v = values as GeneralFormValues;
  return {
    title: v.title,
    distribution_channel: asCodeArray(v.distribution_channel),
    business_segmentation: asCodeArray(v.business_segmentation),
    product_highlight_type,
    type,
    business_types: toBusinessTypeObjects(v.business_types, businessTypeMap),
    shiptos: v.shiptos,
  };
};
